import { Email, Phone, Draw } from "@mui/icons-material";
import type { ProfileDisplayProps } from "../types";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";

const ProfileDisplay: React.FC<ProfileDisplayProps> = ({ phoneNumber, emailAddress, signature }) => {
	const { t } = useTranslation();
	const emailRef = useRef<HTMLSpanElement>(null);
	const [isEmailTruncated, setIsEmailTruncated] = useState(false);
	const [isEmailExpanded, setIsEmailExpanded] = useState(false);

	useEffect(() => {
		const checkTruncation = () => {
			const emailElement = emailRef.current;
			if (!emailElement) {
				setIsEmailTruncated(false);
				return;
			}
			setIsEmailTruncated(emailElement.scrollWidth > emailElement.clientWidth);
		};

		checkTruncation();
		window.addEventListener("resize", checkTruncation);

		return () => {
			window.removeEventListener("resize", checkTruncation);
		};
	}, [emailAddress]);

	const toggleEmailExpanded = () => {
		if (isEmailTruncated) {
			setIsEmailExpanded(prev => !prev);
		}
	};

	return (
		<>
			<div className="flex w-full items-center bg-[#F7F9FB] rounded-xl px-4 py-3">
				<Phone className="w-6 h-6 mr-3 text-gray-700 shrink-0" />
				<span className="text-lg text-gray-900 min-w-0 flex-1 truncate" title={phoneNumber || ""}>
					{phoneNumber || t("psdaCommon.common.notAvailable")}
				</span>
			</div>

			<div className="flex w-full items-center bg-[#F7F9FB] rounded-xl px-4 py-3">
				<Email className="w-6 h-6 mr-3 text-gray-700 shrink-0" />
				<span
					ref={emailRef}
					className={`text-lg text-gray-900 min-w-0 flex-1 ${isEmailExpanded ? "break-all whitespace-normal" : "truncate"} ${
						isEmailTruncated ? "cursor-pointer" : ""
					}`}
					title={isEmailTruncated ? emailAddress || "" : ""}
					onClick={toggleEmailExpanded}
					onKeyDown={event => {
						if (event.key === "Enter" || event.key === " ") {
							event.preventDefault();
							toggleEmailExpanded();
						}
					}}
					role={isEmailTruncated ? "button" : undefined}
					tabIndex={isEmailTruncated ? 0 : -1}>
					{emailAddress || t("psdaCommon.common.notAvailable")}
				</span>
			</div>

			<div className="w-full bg-[#F7F9FB] rounded-xl px-4 py-3">
				<div className="flex items-center mb-2">
					<Draw className="w-6 h-6 mr-3 text-gray-700 shrink-0" />
					<span className="text-sm font-medium text-gray-700">
						{t("psdaCommon.profile.form.signature.label")}
					</span>
				</div>
				{signature ? (
					<div className="w-full bg-white rounded-lg border border-gray-200 p-2">
						<img
							src={signature}
							alt={t("psdaCommon.profile.form.signature.label")}
							className="max-h-24 w-full object-contain"
						/>
					</div>
				) : (
					<span className="text-gray-500 italic">
						{t("psdaCommon.profile.form.signature.noSignature")}
					</span>
				)}
			</div>
		</>
	);
};

export default ProfileDisplay;
